import { OrgData } from "@p0security/shared/types/tenant/index";
import { VERIFIED_EMAIL_REQUIRED } from "@p0security/shared/util/auth";
import {
  SESSION_START_ITEM_KEY,
  TOKEN_EXPIRATION_KEY,
} from "components/Session/useSessionTimeout";
import { FirebaseError } from "firebase/app";
import { AuthCredential, User, UserCredential } from "firebase/auth";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import AuthConst from "../../../constants/AuthConst";
import { idPlatform } from "../IdentityPlatform";
import { PasswordLoginInputs } from "../components/LoginForm";
import { ORG_SLUGS_LOCALSTORAGE_KEY, OrgFromLocalStorageMap } from "../hook.ts";
import { AuthClient, AuthData, Tenant } from "../types/auth";
import { createNormalizedUserFromFirebase } from "../utils/normalizeUser";

export const useFirebaseAuth = (
  tenant: Tenant,
  orgData: OrgData | undefined,
  orgSlug: string | undefined
): AuthClient & AuthData => {
  /** The user object as returned by the IdP */
  const [user, setUser] = useState<User | undefined>();
  /** The user credential from last successful auth */
  const [userCredential, setUserCredential] = useState<UserCredential>();
  const [needsEmailVerification, setNeedsEmailVerification] =
    useState<boolean>(false);

  /** True if system is currently negotiating auth with the IdP */
  const [isInAuth, setIsInAuth] = useState(true);
  const navigate = useNavigate();

  // Use ref instead of global variable to handle React strict mode double rendering
  const authCredentialRef = useRef<AuthCredential | UserCredential>();

  const tenantId = useMemo(
    () => (tenant.state === "found" ? tenant.tenantId : undefined),
    [tenant]
  );

  const isTenantReady = useMemo(
    () => tenant.state === "found" && orgSlug !== undefined,
    [tenant.state, orgSlug]
  );

  // Effect 1: Handle auth state changes (login/logout events)
  useEffect(() => {
    return idPlatform.onAuthStateChanged((u) => {
      if (u && u.tenantId) {
        setUser(u);
      } else {
        setUser(undefined);
      }

      setIsInAuth(false);
    });
  }, []);

  /**
   * A user only needs verification if they are using a sign-in method that requires
   * verified email such as password, phone or custom as defined as VERIFIED_EMAIL_REQUIRED.
   */
  useEffect(() => {
    const requiresVerification =
      user?.providerData?.some((p) =>
        VERIFIED_EMAIL_REQUIRED.includes(p.providerId)
      ) && !user?.emailVerified;

    setNeedsEmailVerification(Boolean(requiresVerification));
  }, [user]);

  const signOut = useCallback(async () => {
    if (user === undefined || isInAuth || tenant.state === "loading") {
      return;
    }
    try {
      sessionStorage.removeItem(SESSION_START_ITEM_KEY);
      sessionStorage.removeItem(TOKEN_EXPIRATION_KEY);
      await idPlatform.signOut();
      setUser(undefined);
      navigate("/");
    } catch (err) {
      if (err instanceof FirebaseError) {
        alert(`Can't sign out:\n${err.code}\n${err.message}`);
      } else {
        throw err;
      }
    }
  }, [user, isInAuth, tenant.state, navigate]);

  // Effect 2: Sign out when tenant is not found
  useEffect(() => {
    if (tenant.state === "not_found") {
      void signOut();
    }
  }, [signOut, tenant.state]);

  // Effect 3: Sign out when user tenantId changes
  useEffect(() => {
    if (user && user.tenantId !== tenantId && user.tenantId) {
      void signOut();
    }
  }, [user, tenantId, signOut]);

  // Effect 4: Store org slug in local storage
  useEffect(() => {
    if (orgSlug && tenant.state === "found" && user !== undefined) {
      const storedOrgs = localStorage.getItem(ORG_SLUGS_LOCALSTORAGE_KEY);
      const parsed: OrgFromLocalStorageMap = storedOrgs
        ? JSON.parse(storedOrgs)
        : {};

      parsed[orgSlug] = {
        lastAccess: new Date(),
      };
      localStorage.setItem(ORG_SLUGS_LOCALSTORAGE_KEY, JSON.stringify(parsed));
    }
  }, [orgSlug, tenant.state, user]);

  /**
   * Wraps an auth operation with common error handling and state management
   */
  const withAuthOperation = useCallback(
    async (
      operation: () => Promise<void>,
      errorMessage = "Operation failed"
    ): Promise<boolean> => {
      if (!isTenantReady) {
        return false;
      }

      try {
        setIsInAuth(true);
        sessionStorage.setItem(SESSION_START_ITEM_KEY, Date.now().toString());
        await operation();
        setIsInAuth(false);
        return true;
      } catch (err) {
        setIsInAuth(false);
        if (err instanceof FirebaseError) {
          alert(`${errorMessage}:\n${err.code}:\n${err.message}`);
        } else {
          throw err;
        }
        return false;
      }
    },
    [isTenantReady]
  );

  const finishSignInWithEmail = useCallback(async (tenantId: string) => {
    const email = window?.localStorage.getItem(
      AuthConst.USER_EMAIL_LOCALSTORAGE_KEY
    );

    if (email !== null) {
      setIsInAuth(true);
      try {
        const credential = await idPlatform.finishEmailSignIn(email, tenantId);
        setUserCredential(credential);
        window?.localStorage.removeItem(AuthConst.USER_EMAIL_LOCALSTORAGE_KEY);
      } catch (_err: any) {
        throw AuthConst.ERRORS.EMAIL_SIGN_IN_ERROR;
      }
    } else {
      throw AuthConst.ERRORS.MISSING_EMAIL;
    }
  }, []);

  /**
   * Attempts to sign in a user with the tenant's configured SSO provider.
   * Sets the "inAuth" state to true.
   */
  const signInWithProvider = useCallback(async () => {
    if (!isTenantReady || tenant.state !== "found" || !orgSlug) {
      return;
    }

    try {
      setIsInAuth(true);
      sessionStorage.setItem(SESSION_START_ITEM_KEY, Date.now().toString());

      const credential = await idPlatform.signIn(tenant, {
        orgSlug,
      });

      if (credential && "user" in credential) {
        setUserCredential(credential);
      } else {
        authCredentialRef.current = credential || undefined;
      }
    } catch (err) {
      setIsInAuth(false);
      if (err instanceof FirebaseError) {
        alert(`Invalid login:\n${err.code}:\n${err.message}`);
      } else {
        throw err;
      }
    }
  }, [orgSlug, tenant, isTenantReady]);

  const signInWithEmail = useCallback(
    async (email: string) => {
      if (tenant.state !== "found" || !orgSlug) {
        return false;
      }

      return withAuthOperation(async () => {
        await idPlatform.signIn(tenant, { email, orgSlug });
        window.localStorage.setItem(
          AuthConst.USER_EMAIL_LOCALSTORAGE_KEY,
          email
        );
      }, "Invalid login");
    },
    [tenant, orgSlug, withAuthOperation]
  );

  const signInWithPassword = useCallback(
    async ({ email, password }: PasswordLoginInputs) => {
      if (tenant.state !== "found" || !orgSlug) {
        return false;
      }

      return withAuthOperation(async () => {
        await idPlatform.signIn(tenant, {
          email,
          password,
          orgSlug,
        });
      }, "Invalid login");
    },
    [tenant, orgSlug, withAuthOperation]
  );

  const signUpWithPassword = useCallback(
    async ({ email, password }: PasswordLoginInputs) => {
      if (tenant.state !== "found" || !orgSlug) {
        return false;
      }

      return withAuthOperation(async () => {
        await idPlatform.signUp(tenant, {
          email,
          password,
          orgSlug,
        });
      }, "Failed to sign up");
    },
    [tenant, orgSlug, withAuthOperation]
  );

  const sendPasswordReset = useCallback(
    async (email: string) => {
      if (tenant.state !== "found" || !orgSlug) {
        return false;
      }

      return withAuthOperation(async () => {
        await idPlatform.sendPasswordReset(tenant, {
          email,
          orgSlug,
        });
      }, "Failed to reset password");
    },
    [tenant, orgSlug, withAuthOperation]
  );

  const resendEmailVerification = useCallback(async () => {
    if (!isTenantReady || tenant.state !== "found" || !user || !orgSlug) {
      return false;
    }

    return withAuthOperation(async () => {
      await idPlatform.resendEmailVerification(tenant, {
        user,
        orgSlug,
      });
    });
  }, [isTenantReady, tenant, user, orgSlug, withAuthOperation]);

  const normalizedUser = createNormalizedUserFromFirebase(user);

  const authData: AuthData = {
    user: tenant.state === "found" ? normalizedUser : undefined,
    userCredential,
    tenant,
    orgSlug,
    orgData,
  };

  const authClient: AuthClient = {
    authCredential: authCredentialRef.current,
    isInAuth,
    needsEmailVerification,
    finishSignInWithEmail,
    signInWithEmail,
    signInWithProvider,
    signInWithPassword,
    signUpWithPassword,
    resendEmailVerification,
    sendPasswordReset,
    signOut,
  };

  return { ...authClient, ...authData };
};
